// src/utils/guidedPath.js
//
// Shared "what next" logic. The Guided Path on the dashboard and the
// assessment completion screen both build their node list from here, so a
// user is pointed at the SAME next step the moment they finish an assessment
// and again when they land on the hub.
//
// Also owns the hand-off between the path and the content it sends people to:
// tapping a node stores it (sessionStorage) so TopicView can show "why you're
// here" and auto-complete the step when a video finishes.

import { doc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { RECOVERY_NODES, MILESTONE_ROUTES, METRIC_ROUTES } from '../data/guidedPathRoutes';

// pathProgress is a map of { [nodeId]: true }; tolerate the legacy array form.
export const normalizePathProgress = (raw) => {
    if (Array.isArray(raw)) return raw.reduce((acc, id) => ({ ...acc, [id]: true }), {});
    return (raw && typeof raw === 'object') ? raw : {};
};

// Build the ordered node list from a calculateScores() result.
// Weekly pulse milestone first (base camp), then recovery-first nodes when
// running hot / at risk / safety-flagged, then the bottom-3 metrics (lowest
// first). The last node is the summit.
// Nodes carry `iconKey` (string) — components map it to an icon.
export const buildPathNodes = ({ scores, isPulseDue }) => {
    const { hasData, insights, quadrant, safetyFlag, wellbeingTier } = scores || {};
    if (!hasData) return [];

    const list = [];
    const usedRoutes = new Set();

    // The weekly pulse is the FIRST checkpoint (base camp): the climb below is
    // built from it, so when it's due it is "you are here" and gates the content
    // steps; once taken it reads done and the trail fills past it.
    list.push({
        id: 'weekly-pulse',
        title: 'Weekly Pulse Check-in',
        desc: isPulseDue
            ? 'Start here — a 10-second pulse updates your climb and keeps your streak alive.'
            : "Done for this week — the steps ahead are built from it. Your next pulse unlocks in a few days.",
        iconKey: 'pulse',
        route: isPulseDue ? MILESTONE_ROUTES.due : MILESTONE_ROUTES.current,
        milestone: true
    });

    const recoveryFirst = quadrant?.id === 'running_hot' || safetyFlag || wellbeingTier === 'At Risk';
    if (recoveryFirst) {
        RECOVERY_NODES.forEach(node => {
            list.push({
                id: node.id,
                title: node.title,
                desc: node.desc,
                iconKey: node.iconKey,
                route: node.route,
                recovery: true
            });
            usedRoutes.add(node.route);
        });
    }

    (insights?.low || []).forEach(metric => {
        if (!metric?.route || usedRoutes.has(metric.route)) return;
        usedRoutes.add(metric.route);
        list.push({
            id: `metric-${metric.id}`,
            metricId: metric.id,
            label: metric.label,
            title: `Strengthen ${metric.label}`,
            desc: `${metric.label} is one of your lower areas right now. A short module here is the quickest win.`,
            iconKey: metric.id,
            route: metric.route
        });
    });

    return list;
};

// The milestone completes itself via the weekly loop; other nodes read pathProgress.
export const isPathNodeComplete = (node, completedMap, { hasData, isPulseDue }) =>
    node.milestone ? (!!hasData && !isPulseDue) : !!completedMap[node.id];

export const pickRecommendedNode = (nodes, completedMap, ctx) =>
    nodes.find(n => !isPathNodeComplete(n, completedMap, ctx)) || null;

// Every checkpoint id that a topic can complete: the metric node(s) routed to
// it plus any recovery node that lands there. Used when a checklist/worksheet
// for the topic is marked complete, however the user got there.
export const pathNodeIdsForTopic = (topicId) => {
    if (!topicId) return [];
    const ids = Object.entries(METRIC_ROUTES).filter(([, m]) => m.route === topicId).map(([id]) => `metric-${id}`);
    RECOVERY_NODES.forEach(n => { if (n.route === topicId) ids.push(n.id); });
    return ids;
};

// --- Path -> content hand-off ----------------------------------------------
const ACTIVE_KEY = 'eri_active_path_node';

export const setActivePathNode = (node) => {
    if (!node) return;
    try {
        sessionStorage.setItem(ACTIVE_KEY, JSON.stringify({
            id: node.id, title: node.title, route: node.route, milestone: !!node.milestone
        }));
    } catch (e) { /* storage unavailable — hand-off is a nicety, not required */ }
};

export const getActivePathNode = () => {
    try {
        const raw = sessionStorage.getItem(ACTIVE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
};

export const clearActivePathNode = () => {
    try { sessionStorage.removeItem(ACTIVE_KEY); } catch (e) { /* noop */ }
};

// Is the stored node the reason the user is on this screen? Matches either the
// node's route (the tab id) against the current hash, or against the topic id
// (AthletePortal aliases e.g. 'mental-health' -> 'anxiety').
export const activePathNodeFor = (topicId) => {
    const node = getActivePathNode();
    if (!node || node.milestone) return null;
    const hash = (window.location.hash || '').replace('#', '');
    return (node.route === topicId || (hash && node.route === hash)) ? node : null;
};

// --- Preview mode ------------------------------------------------------------
// Super-admin preview renders a synthetic user (uid 'preview-user', built in
// App.jsx) that never reads Firestore back, so path progress is kept in-session
// instead: sessionStorage + a window event App listens to. Also stops preview
// from writing junk users/preview-user docs.
export const PREVIEW_UID = 'preview-user';
export const PREVIEW_PROGRESS_EVENT = 'eri:preview-path-progress';
const PREVIEW_PROGRESS_KEY = 'eri_preview_path_progress';

export const readPreviewPathProgress = () => {
    try {
        const raw = sessionStorage.getItem(PREVIEW_PROGRESS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
};

// "Open this worksheet when the topic page mounts" (Guided Path -> sheet).
const PENDING_WS_KEY = 'eri_pending_worksheet';
export const setPendingWorksheet = (worksheetId) => {
    try { sessionStorage.setItem(PENDING_WS_KEY, worksheetId); } catch (e) { /* noop */ }
};
export const takePendingWorksheet = () => {
    try {
        const id = sessionStorage.getItem(PENDING_WS_KEY);
        if (id) sessionStorage.removeItem(PENDING_WS_KEY);
        return id || null;
    } catch (e) { return null; }
};

export const markPathNodeDone = async (uid, nodeId, done = true) => {
    if (!uid || !nodeId) return;
    if (uid === PREVIEW_UID) {
        const next = { ...readPreviewPathProgress(), [nodeId]: done };
        try { sessionStorage.setItem(PREVIEW_PROGRESS_KEY, JSON.stringify(next)); } catch (e) { /* noop */ }
        window.dispatchEvent(new CustomEvent(PREVIEW_PROGRESS_EVENT, { detail: next }));
        return;
    }
    await setDoc(doc(db, 'users', uid), { pathProgress: { [nodeId]: done } }, { merge: true });
};
